import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddRelayDialog } from '../drives/AddRelayDialog';
import { McpConnectionFormDialog } from '../mcp/McpConnectionFormDialog';
import { INTEGRATION_KINDS, IntegrationKindDefinition, integrationHref } from './integration-kinds';
import type { IntegrationRow } from './integration-rows';
import { startConnectInNewTab } from './start-connect-in-new-tab';
import { useOauthConnectStarters } from './useOauthConnectStarters';

type AddIntegrationMenuProps = {
  memberId: number;
  isHubAdmin: boolean;
  rows: IntegrationRow[];
};

export function AddIntegrationMenu({ memberId, isHubAdmin, rows }: AddIntegrationMenuProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [relayOpen, setRelayOpen] = useState(false);
  const [mcpOpen, setMcpOpen] = useState(false);
  const startByKind = useOauthConnectStarters();

  const connectedRowFor = (definition: IntegrationKindDefinition): IntegrationRow | undefined =>
    definition.maxPerScope === 1 ? rows.find((row) => row.kind === definition.kind) : undefined;

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const visible = INTEGRATION_KINDS.filter(
      (definition) =>
        (definition.scope === 'member' || isHubAdmin) &&
        (!term ||
          definition.label.toLowerCase().includes(term) ||
          definition.vendor.toLowerCase().includes(term) ||
          definition.blurb.toLowerCase().includes(term) ||
          definition.category.toLowerCase().includes(term))
    );
    const categories = [...new Set(visible.map((definition) => definition.category))];
    return categories.map((category) => ({
      category,
      items: visible.filter((definition) => definition.category === category),
    }));
  }, [isHubAdmin, search]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
    }
  };

  const handleSelect = (definition: IntegrationKindDefinition) => {
    const connected = connectedRowFor(definition);
    if (connected) {
      handleOpenChange(false);
      navigate(connected.href);
      return;
    }
    if (definition.kind === 'relay') {
      handleOpenChange(false);
      setRelayOpen(true);
      return;
    }
    if (definition.kind === 'mcp') {
      handleOpenChange(false);
      setMcpOpen(true);
      return;
    }
    startConnectInNewTab({ label: definition.label, start: startByKind[definition.kind] });
    handleOpenChange(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button className="gap-2 h-9 px-3" disabled={memberId <= 0}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-2">
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search integrations..."
              aria-label="Search integration kinds"
              className="h-9 pl-10"
            />
          </div>
          {groups.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">No integrations match your search.</p>
          ) : null}
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {groups.map((group) => (
              <div key={group.category}>
                <p className="px-2 pb-1 pt-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {group.category}
                </p>
                {group.items.map((definition) => (
                  <button
                    key={definition.kind}
                    type="button"
                    onClick={() => handleSelect(definition)}
                    className="flex w-full items-start gap-3 rounded-md px-2 py-2 text-left hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                  >
                    <img src={definition.logo} alt="" className="mt-0.5 h-5 w-5 shrink-0 object-contain" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{definition.label}</span>
                      <span className="block text-xs text-muted-foreground">{definition.blurb}</span>
                    </span>
                    {connectedRowFor(definition) ? (
                      <span className="shrink-0 text-xs text-muted-foreground">Connected</span>
                    ) : null}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <AddRelayDialog
        memberId={memberId}
        open={relayOpen}
        onOpenChange={setRelayOpen}
        onClosed={(created) => {
          if (created) {
            navigate(integrationHref('relay', created.relay.id));
          }
        }}
      />
      <McpConnectionFormDialog
        open={mcpOpen}
        onOpenChange={setMcpOpen}
        connection={null}
        onCreated={(created) => navigate(integrationHref('mcp', created.id))}
      />
    </>
  );
}
